import React, { useState, useRef, useEffect } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { FaUser } from "react-icons/fa";
import { IoIosSend } from "react-icons/io";
import { get, capitalize, isEmpty } from "lodash";
import clsx from "clsx";
import { formatPrice } from "@/helpers/helpers";
import AutoResizeTextarea from "@/components/common/AutoResizeTextarea";
import { isMobile } from "react-device-detect";

const DELETED_COMMENT_TEXT = "Comment deleted";

interface UserGuess {
    prediction?: string;
    guess?: number;
}

interface UserData {
    id?: string;
    nickname?: string;
    userGuesses?: UserGuess[];
}

interface TagData {
    name?: string;
}

interface TagVoteData {
    voteType?: "agree" | "disagree";
}

interface CommentItem {
    id: string;
    content: string;
    user?: UserData;
    avatar?: string;
    name?: string;
    created_at: string;
    replies?: CommentItem[];
    Tag?: TagData;
    TagVote?: TagVoteData;
    parent_id?: string | null;
}

interface CommentProps {
    id: string;
    comment: CommentItem;
    maxLines?: number;
    showSeeMore?: boolean;
    depth?: number;
    onSubmitReply?: (payload: { content: string; parentId: string }) => void;
    onDelete?: (commentId: string) => void;
    onReply: (commentId: string, content: string | null) => void;
    isExpanded?: boolean;
    enableReplies?: boolean;
    enableNestedReplies?: boolean;
    showReplyButton?: boolean;
    showViewMoreButton?: boolean;
    showDeleteButton?: boolean;
    isReplyingTo?: boolean;
    replyingTo?: string;
    currentUserId?: string | null;
    isLastReply?: boolean;
    hasParent?: boolean;
    hasChildren?: boolean;
    tag?: any;
    tagVote?: "agree" | "disagree" | null;
}

/**
 * Comment component displays a single comment with user information and content
 */
const Comment: React.FC<CommentProps> = ({
    id,
    comment,
    maxLines = 3,
    showSeeMore = true,
    depth = 0,
    onSubmitReply,
    onDelete,
    onReply,
    isExpanded = false,
    enableReplies = true,
    enableNestedReplies = true,
    showReplyButton = true,
    showViewMoreButton = true,
    showDeleteButton = false,
    isReplyingTo = false,
    replyingTo,
    currentUserId = null,
    isLastReply = false,
    hasParent = false,
    hasChildren = false,
    tag = null,
    tagVote = null,
}) => {
    const navigate = useNavigate();
    const [isContentExpanded, setIsContentExpanded] = useState(false);
    const [shouldShowMore, setShouldShowMore] = useState(false);
    const [showAllReplies, setShowAllReplies] = useState(isExpanded);
    const [replyText, setReplyText] = useState("");
    const [isReplying, setIsReplying] = useState(false);
    const contentRef = useRef<HTMLParagraphElement | null>(null);
    const replyInputRef = useRef<HTMLTextAreaElement | null>(null);
    const userGuess = get(comment, "user.userGuesses[0].prediction", "");
    const userGuessPrice = get(comment, "user.userGuesses[0].guess", 0);
    const replies = get(comment, "replies", []) as CommentItem[];
    const isCurrentUserComment = currentUserId === get(comment, "user.id");
    const isDeleted = comment.content === DELETED_COMMENT_TEXT;
    const tagNameData = get(comment, "Tag.name", null);
    const tagVoteData = get(comment, "TagVote", null) as TagVoteData | null;

    useEffect(() => {
        if (contentRef.current) {
            const element = contentRef.current;
            setShouldShowMore(element.scrollHeight > element.clientHeight);
        }
    }, [comment.content]);

    useEffect(() => {
        if (isReplying && replyInputRef.current) {
            replyInputRef.current.focus();
        }
    }, [isReplying]);

    const getUserGuessColor = (guess: string) => {
        switch (guess.toLowerCase()) {
            case "higher":
                return "tw-text-[#7CC836]";
            case "lower":
                return "tw-text-[#EA4949]";
            case "exact":
                return "tw-text-[#13C5FC]";
            default:
                return "tw-text-gray-500";
        }
    };

    const handleUserClick = () => {
        if (comment.user?.id) {
            navigate(`/social/profile/${comment.user.id}`);
        }
    };

    const toggleExpand = () => {
        setIsContentExpanded(!isContentExpanded);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleExpand();
        }
    };

    // Submit reply via desktop
    const handleReplySubmit = () => {
        if (replyText.trim() && onSubmitReply) {
            onSubmitReply({ content: replyText, parentId: comment.id });
            setReplyText("");
            setIsReplying(false);
        }
    };

    const handleReplyClick = () => {
        if (isMobile) {
            // On mobile, pass the comment ID and whether we're starting or canceling a reply
            onReply(comment.id, isReplyingTo ? null : "");
        } else {
            // On desktop, toggle the reply input field
            setIsReplying(!isReplying);
            if (isReplying) {
                // If we're canceling, notify the parent
                onReply(comment.id, null);
            } else {
                // If we're starting a reply, notify the parent
                onReply(comment.id, "");
            }
        }
    };

    const renderReplies = () => {
        if (!replies || replies.length === 0 || !enableReplies) return null;

        // Check if any reply has nested replies
        const hasNestedReplies = replies.some((reply) => reply.replies && reply.replies.length > 0);
        const isSingleReplyWithoutNestedReplies = replies.length === 1 && !hasNestedReplies;

        if (enableNestedReplies) {
            if (!showAllReplies && replies.length > 0) {
                // Show only the first reply and the 'View X more replies...' button
                return (
                    <div className="tw-relative tw-ml-6">
                        {/* Vertical line for the reply group, connecting all siblings */}
                        <div
                            className="tw-absolute tw-left-0 tw-top-0 tw-bottom-0 tw-w-[2px] tw-bg-gray-300"
                            style={{ zIndex: 0 }}
                        />
                        <div>
                            <div className="tw-relative">
                                <Comment
                                    id={`comment-${replies[0].id}`}
                                    comment={replies[0]}
                                    depth={depth + 1}
                                    onReply={onReply}
                                    isExpanded={false}
                                    enableReplies={enableNestedReplies}
                                    enableNestedReplies={false}
                                    showReplyButton={showReplyButton}
                                    showViewMoreButton={showViewMoreButton}
                                    showDeleteButton={true}
                                    currentUserId={currentUserId ?? undefined}
                                    onSubmitReply={onSubmitReply}
                                    onDelete={onDelete}
                                    replyingTo={replyingTo}
                                    isReplyingTo={replyingTo === comment.id}
                                    isLastReply={replies.length === 1}
                                    hasParent={true}
                                    hasChildren={!!(replies[0].replies && replies[0].replies.length > 0)}
                                />
                                {/* For the first reply, cover the vertical line below the avatar if it's the only reply */}
                                {isSingleReplyWithoutNestedReplies && (
                                    <div
                                        className="tw-absolute tw-left-0 tw-w-[2px] tw-bg-white"
                                        style={{ zIndex: 2, top: `calc(${avatarCenter}px)`, bottom: 0 }}
                                    />
                                )}
                            </div>
                            {/* Show view more button if there are multiple replies or if the single reply has nested replies */}
                            {showViewMoreButton && (replies.length > 1 || hasNestedReplies) && (
                                <div
                                    className="tw-pl-4 tw-mt-1 tw-text-[13px] tw-text-[#65676B] hover:tw-underline tw-cursor-pointer tw-font-bold"
                                    onClick={() => setShowAllReplies(true)}
                                >
                                    {replies.length > 1
                                        ? `View ${replies.length - 1} more ${replies.length === 2 ? "reply" : "replies"}...`
                                        : "View replies..."}
                                </div>
                            )}
                        </div>
                    </div>
                );
            }
            // Show all replies if showAllReplies is true
            return (
                <div className="tw-relative tw-ml-6">
                    {/* Vertical line for the reply group, connecting all siblings */}
                    <div
                        className="tw-absolute tw-left-0 tw-top-0 tw-bottom-0 tw-w-[2px] tw-bg-gray-300"
                        style={{ zIndex: 0 }}
                    />
                    <div>
                        {replies.map((reply, idx) => (
                            <div key={reply.id} className="tw-relative">
                                <Comment
                                    id={`comment-${reply.id}`}
                                    comment={reply}
                                    depth={depth + 1}
                                    onReply={onReply}
                                    isExpanded={true}
                                    enableReplies={enableNestedReplies}
                                    enableNestedReplies={true}
                                    showReplyButton={showReplyButton}
                                    showViewMoreButton={showViewMoreButton}
                                    showDeleteButton={true}
                                    currentUserId={currentUserId ?? undefined}
                                    onSubmitReply={onSubmitReply}
                                    onDelete={onDelete}
                                    replyingTo={replyingTo}
                                    isReplyingTo={replyingTo === comment.id}
                                    isLastReply={idx === replies.length - 1}
                                    hasParent={true}
                                    hasChildren={!!(reply.replies && reply.replies.length > 0)}
                                />
                                {/* For the last reply, cover the vertical line below the avatar */}
                                {idx === replies.length - 1 && (
                                    <div
                                        className="tw-absolute tw-left-0 tw-w-[2px] tw-bg-white"
                                        style={{ zIndex: 2, top: `calc(${avatarCenter}px)`, bottom: 0 }}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            );
        }
        return null;
    };

    // Calculate profile picture size based on nesting depth
    const profileSize = Math.max(32 - depth * 4, 16);
    const profileIconSize = Math.max(16 - depth * 2, 8);
    // Helper to get avatar center offset
    const avatarCenter = profileSize / 2;

    return (
        <div id={id} className={clsx("tw-py-2", { "tw-pl-4": depth > 0 })}>
            <div className="tw-relative tw-flex tw-items-start">
                <div className="tw-relative">
                    {/* Horizontal line if this is a child */}
                    {hasParent && (
                        <div
                            className="tw-absolute"
                            style={{
                                left: "-16px", // adjust as needed for your spacing
                                top: "25%",
                                width: "16px",
                                height: "16px",
                                borderLeft: "2px solid #D1D5DB", // gray-300
                                borderBottom: "2px solid #D1D5DB",
                                borderBottomLeftRadius: "8px",
                                transform: "translateY(-50%)",
                                zIndex: 1,
                                background: "transparent",
                            }}
                        />
                    )}
                    {/* Vertical line if this is a parent */}
                    {hasChildren && (
                        <div
                            className="tw-absolute tw-left-1/2 tw-top-full tw-w-[1.5px] tw-bg-gray-300"
                            style={{ height: "20px", zIndex: 0, transform: "translateX(-50%)" }}
                        />
                    )}
                    <div
                        onClick={handleUserClick}
                        className={clsx(
                            "tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-relative tw-z-10 tw-border tw-border-gray-200",
                            { "tw-cursor-pointer": comment.user?.id },
                        )}
                        style={{ width: profileSize, height: profileSize }}
                    >
                        {comment.avatar ? (
                            <img
                                src={comment.avatar}
                                alt={comment.name}
                                className="tw-w-full tw-h-full tw-object-cover"
                            />
                        ) : (
                            <FaUser
                                className="tw-text-gray-400"
                                style={{ width: profileIconSize, height: profileIconSize }}
                            />
                        )}
                    </div>
                </div>
                <div className="tw-flex-1">
                    <div className="tw-font-noto tw-text-[14px] tw-flex tw-flex-col tw-pl-1">
                        <div className="tw-flex tw-items-baseline tw-gap-2 tw-justify-between">
                            <span
                                onClick={handleUserClick}
                                className={clsx("tw-font-bold", {
                                    "tw-cursor-pointer hover:tw-underline": comment.user?.id,
                                })}
                            >
                                {comment?.user?.nickname}
                            </span>
                            {(tagNameData || tagVoteData) && (
                                <span
                                    className={clsx(
                                        "tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-bold",
                                        tagVoteData
                                            ? tagVoteData.voteType === "agree"
                                                ? "tw-bg-[#E6F7D9] tw-text-[#55A809]"
                                                : "tw-bg-[#FBE6E7] tw-text-[#DB2528]"
                                            : "tw-bg-[#F0EDFF] tw-text-[#6c47ff]",
                                    )}
                                >
                                    #{(tag as any)?.name || tagNameData}{" "}
                                    {tagVoteData ? (tagVoteData.voteType === "agree" ? " - Yes" : " - No") : ""}
                                </span>
                            )}
                            {!isEmpty(userGuess) && (
                                <span className={getUserGuessColor(userGuess as string)}>
                                    {userGuessPrice > 0
                                        ? `Guessed ${formatPrice(userGuessPrice)}`
                                        : `Guessed ${capitalize(userGuess as string)}`}
                                </span>
                            )}
                        </div>
                        <div className="tw-relative">
                            {isDeleted ? (
                                <p className="tw-text-[#605E5E] tw-mt-1 tw-mb-1 tw-italic">
                                    This comment has been deleted
                                </p>
                            ) : (
                                <p
                                    ref={contentRef}
                                    className={clsx("tw-text-[#605E5E] tw-mt-1 tw-mb-1", {
                                        [`tw-line-clamp-${maxLines}`]: !isContentExpanded,
                                    })}
                                >
                                    {comment.content}
                                </p>
                            )}
                            {!isDeleted && showSeeMore && shouldShowMore && (
                                <button
                                    onClick={toggleExpand}
                                    onKeyDown={handleKeyDown}
                                    className="tw-bg-white tw-text-blue-500 tw-text-sm tw-p-0 tw-mb-2 hover:tw-text-blue-600"
                                    tabIndex={0}
                                >
                                    {isContentExpanded ? "See less" : "See more"}
                                </button>
                            )}
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-4 tw-mt-1">
                            <span className="tw-text-xs tw-text-[#605E5E]">{moment(comment.created_at).fromNow()}</span>
                            {!isDeleted && showReplyButton && enableReplies && (
                                <button
                                    className={clsx(
                                        "tw-bg-white tw-text-[13px] hover:tw-underline tw-cursor-pointer",
                                        isReplying || (isMobile && replyingTo === comment.id)
                                            ? "tw-text-purple-600 tw-font-bold"
                                            : "tw-text-[#65676B]",
                                    )}
                                    onClick={handleReplyClick}
                                >
                                    Reply
                                </button>
                            )}
                            {!isDeleted && showDeleteButton && isCurrentUserComment && (
                                <button
                                    onClick={() => onDelete && onDelete(comment.id)}
                                    className="tw-bg-white tw-text-xs tw-px-0 tw-text-[#605E5E] hover:tw-text-red-500"
                                >
                                    Delete
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Reply Input - Only show on desktop */}
                    {!isMobile && isReplying && !isDeleted && (
                        <div className="tw-mt-2 tw-flex tw-items-start tw-gap-2">
                            <AutoResizeTextarea
                                ref={replyInputRef as any}
                                value={replyText}
                                onChange={(e: any) => setReplyText(e.target.value)}
                                placeholder="Write a reply..."
                            />
                            <button
                                className="tw-bg-[#F69929] tw-p-2 tw-rounded-full tw-border-[1px] tw-border-solid tw-border-black hover:tw-bg-[#e89355] tw-transition-colors tw-self-end"
                                onClick={handleReplySubmit}
                            >
                                <IoIosSend className="tw-h-[20px] tw-w-[20px] tw-text-black" />
                            </button>
                        </div>
                    )}

                    {/* Render replies using the function */}
                    {renderReplies()}
                </div>
            </div>
        </div>
    );
};

export default Comment;
